import './CreateDialog.scss';

import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ToastContext from '../../../../../context/ToastContext';
import useAxiosHook from '../../../../../hooks/useAxiosHook';
import { warnToast } from '../../../../../utils/helpers/primereact';
import AutoCompleteInput from '../../../../Forms/AutoCompleteInput/AutoCompleteInput';
import FieldWithErrors from '../../../../Forms/ReactHookForm/FieldWithErrors';
import { getPointTypes } from '../../LogisticsRoutes.functions';
import {
  getInitialValues,
  getValidationSchema,
  toApiData,
} from './CreateDialog.functions';
import GeoLocation from './GeoLocation';
import PointRow from './PointRow';

function FormContainer({
  isEditDialog,
  logisticRouteData,
  handleCreation,
  handleEditing,
}) {
  const { t } = useTranslation();
  const { toastRef } = useContext(ToastContext);
  const [searchEmployee, setSearchEmployee] = useState('');

  const validationSchema = useMemo(() => getValidationSchema(t), [t]);

  const defaultValues = useMemo(
    () => getInitialValues(isEditDialog, logisticRouteData),
    [logisticRouteData, isEditDialog]
  );

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  const { control, handleSubmit, reset, setValue, watch } = methods;
  const values = watch();

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const { data: employees, isLoading: isEmployeesLoading } = useAxiosHook(
    `/employees?name=${searchEmployee}`
  );

  const employeesOptions = useMemo(
    () =>
      (employees?.data ?? []).map((item) => ({
        value: item.id,
        label: item.ime + ' ' + item.prezime,
      })) ?? [],
    [employees]
  );

  const pointTypes = useMemo(() => getPointTypes(t), [t]);

  const {
    fields: employeesData,
    append: appendEmployees,
    remove: removeEmployees,
  } = useFieldArray({
    control,
    name: 'employees',
  });

  const {
    fields: pointsFields,
    append: appendPointsFields,
    remove: removePointsFields,
  } = useFieldArray({
    control,
    name: 'points',
  });

  const pointsMap = useMemo(
    () =>
      pointsFields
        .filter((item) => !!item.address?.location)
        .map((item) => {
          const loc = item.address.location;
          const lat = loc.lat ?? loc.coordinates[1];
          const lng = loc.long ?? loc.coordinates[0];
          return [lat, lng];
        }),
    [pointsFields]
  );

  const onSearchEmployee = useCallback(
    (e) => {
      setSearchEmployee(e);
    },
    [setSearchEmployee]
  );

  const handleSelectEmployee = (selectedEmployee) => {
    if (!selectedEmployee) return;

    const exists = values?.employees?.some(
      (emp) => emp.value === selectedEmployee
    );

    if (exists) {
      warnToast(toastRef, t('Warning'), t('Already have this employee'));
    } else {
      const findEmployee = employeesOptions?.find(
        (emp) => emp.value === selectedEmployee
      );

      appendEmployees(findEmployee);
      setSearchEmployee('');
    }
  };

  function handleFormSubmission(values) {
    const params = toApiData(values);
    if (isEditDialog) {
      handleEditing(params);
    } else {
      handleCreation(params);
    }
  }

  function handleDragEnd(result) {
    if (!result.destination) return;

    const src = result.source.index;
    const dst = result.destination.index;
    if (src === dst) return;

    const points = [...values.points];
    const [moved] = points.splice(src, 1);
    points.splice(dst, 0, moved);

    const reordered = points.map((p, i) => ({
      ...p,
      sequence: i + 1,
    }));

    removePointsFields();

    reordered.forEach((p) => appendPointsFields(p));

    setValue('points', reordered);
  }

  return (
    <FormProvider {...methods}>
      <form
        id="create-edit-logistics-route"
        onSubmit={handleSubmit(handleFormSubmission)}
      >
        <div className="general-info-tab p-fluid">
          <span className="section-title">{t('General information')}</span>
          {/* NAME */}
          <FieldWithErrors name="name" label={t('Name')}>
            <Controller
              name="name"
              render={({ field }) => (
                <InputText
                  name="name"
                  id="name"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FieldWithErrors>
        </div>

        {/* POINTS */}
        <FieldWithErrors name="points" label={false}>
          <span className="section-title">{t('Points')}</span>
          <table cellPadding="0" cellSpacing="0">
            {pointsFields?.length > 0 && (
              <thead>
                <tr>
                  <th />
                  <th>{t('Point Type')}</th>
                  <th>{t('Timeframe')}</th>
                  <th>{t('Points')}</th>
                  <th>{t('Actions')}</th>
                </tr>
              </thead>
            )}
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="logistics-route-list">
                {(dropProvided) => (
                  <tbody ref={dropProvided.innerRef}>
                    {pointsFields.map((point, index) => (
                      <PointRow
                        key={point.entityId}
                        index={index}
                        methods={methods}
                        pointTypes={pointTypes}
                        removePointsFields={removePointsFields}
                      />
                    ))}
                    {dropProvided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </DragDropContext>
          </table>
          <div className="points-buttons">
            <Button
              type="button"
              icon="fas fa-plus"
              className="p-button-outlined add-field"
              onClick={() => {
                appendPointsFields({
                  entityId: pointsFields.length + 1,
                  pointName: '',
                  pointTypeId: 1,
                  sequence: pointsFields.length + 1,
                  maintenanceTimeframe: '09:00-16:00',
                  address: null,
                });
              }}
            >
              {t('Add point')}
            </Button>
          </div>
        </FieldWithErrors>

        {/* Employees */}
        <span className="section-title">{t('Transporters')}</span>
        <AutoCompleteInput
          name="employees"
          isLoading={isEmployeesLoading}
          placeholder={t('Search employees')}
          options={employeesOptions}
          filterValue={searchEmployee ?? ''}
          onFilterChange={onSearchEmployee}
          onSelectionChange={(e) => handleSelectEmployee(e)}
          value={''}
          filterDataCy="employees"
          optionsClassName="employees-cy-headquarter"
        />

        <table cellPadding="0" cellSpacing="0" className="table-rows">
          <thead>
            <tr>
              <th>{t('Name')}</th>
              <th>{t('Action')}</th>
            </tr>
          </thead>
          <tbody>
            {employeesData?.map((employee, index) => (
              <tr key={employee.value}>
                <td>{employee.label}</td>
                <td>
                  <FontAwesomeIcon
                    className="title-icon"
                    size="lg"
                    icon={faTrashAlt}
                    onClick={() => removeEmployees(index)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div>
          <span className="section-title">{t('Points')}</span>
          <GeoLocation data={pointsMap} />
        </div>
      </form>
    </FormProvider>
  );
}

export default FormContainer;
